import { Inject, Injectable } from '@angular/core';
import { RdLibModuleHermesModeConfig } from '@rd-lib/rd-lib.module.config';
import {
  CLIENT_ID,
  CONNECTION,
  GUARDHOUSE_API_URL,
  HERMES_MODE,
  REDIRECT_URI,
  SCOPE,
} from '@rd/core/tokens/rd-lib.token';
import { GuardHouseLogoutCodes } from '@rd/types';
import { EnvironmentConnection, StartsWithHttps } from 'global-types';
import {
  MFAAutoRedemption,
  MFAOneTimeRedemption,
  MFAShoppingCart,
} from '../../mfa/types';
import { HermesService } from '../hermes/hermes.service';
import { RedirectUrlSettingService } from '../redirect-url-setting/redirect-url-setting.service';
import { WindowRefService } from '../window-ref/window-ref.service';

export interface LoginParams {
  client_id?: string;
  connection?: string;
  state: string;
  scope: string;
  redirect_uri: string;
  response_type: string;
}

export interface LogoutParams {
  post_logout_redirect_uri?: string;
  state?: string;
  reason?: GuardHouseLogoutCodes;
}

@Injectable({
  providedIn: 'root',
})
export class GuardhouseService {
  constructor(
    @Inject(GUARDHOUSE_API_URL) private guardhouseApiUrl: StartsWithHttps,
    @Inject(CLIENT_ID) private clientId: string,
    @Inject(CONNECTION) private connection: EnvironmentConnection,
    @Inject(SCOPE) private scope: string,
    @Inject(REDIRECT_URI) private redirectUri: StartsWithHttps,
    @Inject(HERMES_MODE) private hermesMode: RdLibModuleHermesModeConfig,
    private redirectUrlSettingService: RedirectUrlSettingService,
    private windowRef: WindowRefService,
    private hermesService: HermesService,
  ) {}

  redirectToMFAUrl(
    hydrationState: MFAAutoRedemption | MFAOneTimeRedemption | MFAShoppingCart,
    redirectUrl: string,
    level: number,
  ): void {
    const nonce = this.generateRandomNonce();
    this.hermesService.saveStateWithData(nonce, hydrationState);

    this.windowRef.nativeWindow.location.href = this.getMFAUrl(
      nonce,
      redirectUrl,
      level,
    ).toString();
  }

  redirectToEnrollUrl(): void {
    const nonce = this.generateRandomNonce();
    this.hermesService.saveState(nonce);

    this.windowRef.nativeWindow.location.href =
      this.getEnrollUrl(nonce).toString();
  }

  redirectToLoginUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete('redirect_required');

    const locationPathName =
      this.redirectUrlSettingService.getLoginRedirectUrl();

    const currentUrl = urlParams.toString()
      ? `${locationPathName}?${urlParams.toString()}`
      : locationPathName;
    const nonce = this.generateRandomNonce();

    this.hermesService.saveState(nonce, currentUrl);

    if (this.hermesMode === 'iframe') {
      this.windowRef.nativeWindow.location.href =
        this.getLoginUrl(nonce).toString();
    } else if (this.hermesMode === 'refresh-token-rotation') {
      this.hermesService.login({ nonce, appState: { currentUrl } });
    }
  }

  redirectToLogoutUrl(reason: GuardHouseLogoutCodes): void {
    const nonce = this.generateRandomNonce();
    this.hermesService.saveState(nonce);

    this.hermesService.removeRefreshToken();
    this.windowRef.nativeWindow.location.href = this.getLogoutUrl(
      nonce,
      reason,
    ).toString();
  }

  redirectToChangePasswordUrl(): void {
    this.windowRef.nativeWindow.location.href =
      this.changePasswordUrl.toString();
  }

  redirectToProgramLinkingUrl(url: string): void {
    const nonce = this.generateRandomNonce();
    this.hermesService.saveState(nonce);

    this.windowRef.nativeWindow.location.href = url;
  }

  get changePasswordUrl(): URL {
    return new URL('password/edit', this.guardhouseApiUrl);
  }

  get changeEmailUrl(): URL {
    return new URL('username/edit', this.guardhouseApiUrl);
  }

  get logoutUrl(): URL {
    return new URL('logout', this.guardhouseApiUrl);
  }

  get enrollUrl(): URL {
    return new URL('activate', this.guardhouseApiUrl);
  }

  get profileUrl(): URL {
    return new URL('profile', this.guardhouseApiUrl);
  }

  private getMFAUrl(nonce: string, redirectUrl: string, level: number): URL {
    const params = {
      state: nonce,
      redirect_uri: location.origin + redirectUrl,
      level,
    };

    return new URL(
      `step_up?${this.convertToQueryString(params)}`,
      this.guardhouseApiUrl,
    );
  }

  private getEnrollUrl(nonce: string): URL {
    const params: LoginParams = {
      client_id: this.clientId,
      connection: this.connection,
      state: nonce,
      scope: this.scope,
      redirect_uri: this.redirectUri,
      response_type: 'id_token,token',
    };

    return new URL(
      `activate?${this.convertToQueryString(params)}`,
      this.guardhouseApiUrl,
    );
  }

  private getLoginUrl(nonce: string): URL {
    const params: LoginParams = {
      connection: this.connection,
      state: nonce,
      scope: this.scope,
      redirect_uri: this.redirectUri,
      response_type: 'id_token,token',
    };

    return new URL(
      `login?${this.convertToQueryString(params)}`,
      this.guardhouseApiUrl,
    );
  }

  private getLogoutUrl(nonce: string, reason: GuardHouseLogoutCodes): URL {
    const params: LogoutParams = {
      post_logout_redirect_uri: this.generateLogoutRedirectUri(reason),
      state: nonce,
      reason,
    };

    return new URL(
      `logout?${this.convertToQueryString(params)}`,
      this.guardhouseApiUrl,
    );
  }

  private convertToQueryString(params: LoginParams | LogoutParams): string {
    return (
      Object.keys(params)
        // @ts-ignore
        .map((key) => key + '=' + params[key])
        .join('&')
    );
  }

  private generateLogoutRedirectUri(reason: GuardHouseLogoutCodes): string {
    return reason === GuardHouseLogoutCodes.UnIntentionalLogout
      ? `${this.guardhouseApiUrl}/login`
      : `${this.redirectUri}`;
  }

  // generates random value to prevent replay attack. We use time based UUID generation
  private generateRandomNonce(): string {
    return crypto.randomUUID();
  }
}
